import type { CityConfig } from "../city-template/types";

export const escondidoConfig: CityConfig = {
  city: "Escondido",
  state: "California",
  stateAbbr: "CA",
  slug: "escondido",
  route: "/cities/escondido",
  title: "Emergency Passport Escondido, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Escondido, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for North San Diego County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Escondido, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Escondido and North San Diego County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Escondido%20California%20landscape%20with%20rolling%20green%20hills%20and%20inland%20valley%20scenery%2C%20warm%20golden%20hour%20sunset%20light%20over%20the%20San%20Diego%20North%20County%20countryside%2C%20suburban%20residential%20neighborhoods%20with%20citrus%20groves%20and%20chaparral%20vegetation%2C%20distant%20mountain%20ranges%20visible%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20warm%20earth%20tones%20with%20amber%20and%20orange%20sky%20and%20verdant%20valley%20landscape&width=1800&height=700&seq=escondido-hero-001&orientation=landscape",
  heroImageAlt: "Escondido California landscape with rolling hills and inland valley at golden hour",
  geoPosition: "33.1192;-117.0864",
  geoPlacename: "Escondido, California",
  regionalAgency: {
    name: "San Diego Passport Agency",
    address: "401 W A St, San Diego, CA 92101",
    distance: "Located in downtown San Diego — approximately 30–45 minutes from Escondido via I-15",
  },
  nearbyCitiesZips: [
    { name: "Escondido", zips: "92025, 92026, 92027, 92029" },
    { name: "San Marcos", zips: "92069, 92078, 92079" },
    { name: "Vista", zips: "92081, 92083, 92084, 92085" },
    { name: "Poway", zips: "92064" },
    { name: "Oceanside", zips: "92054, 92056, 92057, 92058" },
    { name: "Carlsbad", zips: "92008, 92009, 92010, 92011" },
    { name: "Encinitas", zips: "92024, 92007" },
    { name: "Temecula", zips: "92590, 92591, 92592, 92593" },
    { name: "Murrieta", zips: "92563, 92564" },
    { name: "Ramona", zips: "92065" },
    { name: "Valley Center", zips: "92082" },
    { name: "Fallbrook", zips: "92028" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent North San Diego County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Diego", route: "/cities/san-diego" },
    { name: "Chula Vista", route: "/cities/chula-vista" },
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Long Beach", route: "/cities/long-beach" },
    { name: "Irvine", route: "/cities/irvine" },
    { name: "Santa Ana", route: "/cities/santa-ana" },
    { name: "Anaheim", route: "/cities/anaheim" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Escondido remotely via secure courier. We do not have a walk-in office in Escondido — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for North San Diego County travelers.",
  fpcOfficeDistance: "Serving all of North San Diego County and inland Southern California",
  serviceAreaIntro: "From the historic downtown Grand Avenue to the Lake Hodges area, from the San Pasqual Valley to the Del Dios neighborhood — Fast Passport Center serves the entire Escondido community. We regularly help travelers from San Marcos, Vista, Poway, and Oceanside get their passports fast.",
  nearbyCitiesIntro: "We serve all Escondido neighborhoods and nearby North San Diego County cities including San Marcos, Vista, Oceanside, Carlsbad, and Encinitas. If you are in inland North County or the San Diego coastal corridor, we have you covered.",
  howItWorksIntro: "Most Escondido travelers complete everything online and ship documents overnight. No need to drive to San Diego or wait in line at the regional agency.",
  mistakesIntro: "With San Diego International Airport and McClellan-Palomar Airport serving as the closest international gateways, Escondido travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Escondido and North San Diego County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Escondido? Here are the most common ones we hear from North San Diego County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent North San Diego County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Steven B.",
      location: "Escondido, CA",
      rating: 5,
      text: "My business trip to Hong Kong was in 2 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Incredible turnaround for Escondido.",
      service: "Emergency Passport",
    },
    {
      name: "Maria G.",
      location: "San Marcos, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to Alaska. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin L.",
      location: "Vista, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 48 hours. Amazing service.",
      service: "Lost Passport",
    },
    {
      name: "Patricia N.",
      location: "Oceanside, CA",
      rating: 5,
      text: "As a frequent international traveler from North County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "escondido-1",
      name: "Escondido Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1150 W Valley Pkwy",
      city: "Escondido",
      state: "CA",
      zip: "92025",
      phone: "(760) 745-8623",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Escondido",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "escondido-2",
      name: "San Marcos Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "218 E Mission Rd",
      city: "San Marcos",
      state: "CA",
      zip: "92069",
      phone: "(760) 744-4241",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from Cal State San Marcos",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "escondido-3",
      name: "Vista Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "830 E Vista Way",
      city: "Vista",
      state: "CA",
      zip: "92084",
      phone: "(760) 726-4141",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from downtown Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for North San Diego County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "escondido-4",
      name: "Oceanside Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2015 E Vista Way",
      city: "Oceanside",
      state: "CA",
      zip: "92054",
      phone: "(760) 722-4572",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Oceanside Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for coastal North County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "escondido-5",
      name: "Carlsbad Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2775 El Camino Real",
      city: "Carlsbad",
      state: "CA",
      zip: "92008",
      phone: "(760) 729-4341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Carlsbad Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for coastal North County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "escondido-6",
      name: "San Diego County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1600 Pacific Hwy",
      city: "San Diego",
      state: "CA",
      zip: "92101",
      phone: "(619) 237-0502",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "30 miles from Escondido",
      website: "https://www.sandiegocounty.gov/",
      websiteLabel: "San Diego County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Diego", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "North San Diego County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at North San Diego County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many North San Diego County applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Diego regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many North San Diego County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "North San Diego County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Escondido." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Escondido address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Escondido before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Escondido for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most North San Diego County travelers." },
    { q: "How fast can I renew my passport in Escondido?", a: "Fast Passport Center offers fast passport renewal in Escondido in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Escondido in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Escondido?", a: "Same-day passport appointments are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Escondido residents." },
    { q: "Can I expedite a child passport in Escondido?", a: "Yes. Fast Passport Center expedites child passports in Escondido for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Escondido with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Escondido?",
      a: "With Fast Passport Center's emergency courier service, Escondido travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Diego Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Escondido?",
      a: "Popular options include the Escondido Post Office (92025), San Marcos Post Office (92069), Vista Post Office (92084), and Oceanside Post Office (92054). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Escondido?",
      a: "Same-day passports are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Ramona, Valley Center, or Fallbrook?",
      a: "We serve all of North San Diego County and inland areas including Ramona, Valley Center, Fallbrook, and Bonsall. Our overnight shipping labels work from any UPS location, so your location within North County doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Escondido?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Escondido?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Escondido and San Marcos offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Escondido?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Escondido?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk North San Diego County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Escondido", address: "1150 W Valley Pkwy, Escondido, CA 92025", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — San Marcos", address: "218 E Mission Rd, San Marcos, CA 92069", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Vista", address: "830 E Vista Way, Vista, CA 92084", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Oceanside", address: "2015 E Vista Way, Oceanside, CA 92054", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Carlsbad", address: "2625 El Camino Real, Carlsbad, CA 92008", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Encinitas", address: "127 N El Camino Real, Encinitas, CA 92024", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Poway", address: "13650 Poway Rd, Poway, CA 92064", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — San Marcos", address: "1215 N El Camino Real, San Marcos, CA 92069", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Escondido",
      address: "1450 E Valley Pkwy, Escondido, CA 92027",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — San Marcos",
      address: "590 Rancheros Dr, San Marcos, CA 92069",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Oceanside",
      address: "2353 Vista Way, Oceanside, CA 92054",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — San Diego",
      address: "3960 Normal St, San Diego, CA 92103",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for San Diego County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "CRQ",
      name: "McClellan-Palomar Airport",
      distance: "20 miles from Escondido",
      address: "2192 Palomar Airport Rd, Carlsbad, CA 92011",
      note: "The closest airport to Escondido. Domestic flights and some international connections.",
      flightUrl: "https://www.san.org/",
      flightLabel: "San Diego Airport",
      featured: true,
    },
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "30 miles from Escondido",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "Major international hub for long-haul flights to Asia, Europe, and Latin America. Accessible via I-15.",
      flightUrl: "https://www.san.org/",
      flightLabel: "San Diego Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "90 miles from Escondido",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub. Accessible via I-5 for long-haul flights to Asia, Europe, and South America.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
};